// Path: src/pages/student/StudentLessonDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStudentLessonDetail, type PublicLessonOut } from '../../lib/apis_publish';
import {
  ArrowLeft, Clock, Target, List, PlayCircle,
  CheckCircle2, AlertCircle, FileText
} from 'lucide-react';
import LogoutButton from '../../components/common/LogoutButton';
import ExpandableText from '../../components/common/ExpandableText';

const StudentLessonDetail: React.FC = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState<PublicLessonOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lessonId) return;

    const fetchLessonDetail = async () => {
      setLoading(true);
      try {
        const response = await getStudentLessonDetail(parseInt(lessonId));
        setLesson(response.data);
      } catch (err: any) {
        console.error('Error fetching lesson detail:', err);
        // 检查错误类型，401或403表示权限不足
        if (err.response?.status === 401 || err.response?.status === 403) {
          // 权限不足，跳转到登录页
          setError('您没有权限访问该课时，请重新登录。');
          // 延迟跳转，让用户看到错误信息
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1500);
        } else {
          setError('无法加载课时详情，请检查网络连接。');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLessonDetail();
  }, [lessonId, navigate]);

  // Loading State
  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="text-blue-400 font-mono text-sm animate-pulse">LOADING DATA STREAM...</p>
      </div>
    </div>
  );

  // Error State
  if (error || !lesson) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
      <div className="bg-red-500/10 border border-red-500/50 p-6 rounded-xl max-w-md text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">加载失败</h3>
        <p className="text-gray-400 mb-6">{error || '未找到该课时信息'}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
        >
          返回上一页
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white font-sans selection:bg-blue-500/30">

      {/* 顶部背景装饰 */}
      <div className="fixed top-0 left-0 w-full h-[400px] bg-gradient-to-b from-blue-900/20 to-slate-900 -z-10 pointer-events-none" />

      {/* Header Navigation */}
      <div className="sticky top-0 z-20 bg-slate-900/80 backdrop-blur-md border-b border-white/5 px-6 py-4">
        <div className="container mx-auto max-w-6xl flex justify-between items-center">
          <button
            onClick={() => navigate('/student/portal')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
          >
            <div className="p-2 rounded-full bg-white/5 group-hover:bg-white/10">
              <ArrowLeft size={18} />
            </div>
            <span className="font-medium">返回课程大纲</span>
          </button>
          <div className="flex items-center gap-4">
            {/* VibeCoding 跳转按钮 */}
            <button
              onClick={() => window.open('/student/vibe-coding', '_blank', 'noopener,noreferrer')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium rounded-lg shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
              title="进入 VibeCoding 编程学习"
            >
              <span className="text-xl">🤖</span>
              VibeCoding
            </button>
            <LogoutButton />
          </div>
        </div>
      </div>

      <main className="container mx-auto max-w-6xl px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* 左侧：课时信息概览 (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-8 animate-in slide-in-from-left-4 duration-500">

              {/* 封面图 */}
              <div className="aspect-video rounded-2xl overflow-hidden bg-slate-800 shadow-2xl shadow-blue-900/20 border border-white/10 relative group">
                {lesson.cover_image_url ? (
                  <img
                    src={lesson.cover_image_url}
                    alt={lesson.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  // 同样移除自动隐藏逻辑，确保只要有链接就尝试渲染
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-700">
                    <FileText className="text-white/20 w-16 h-16" />
                  </div>
                )}
                {/* 装饰性光效 */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-60" />
              </div>

              {/* 标题区 */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 border border-white/10 shadow-lg shadow-blue-900/20">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 text-cyan-400 text-xs font-bold tracking-wider backdrop-blur-sm">
                    🚀 LESSON {lesson.sort_order}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-gray-400 bg-white/5 px-3 py-1 rounded-full border border-white/10">
                    <Clock size={12} /> 45 MINS
                  </span>
                </div>
                <h1 className="text-3xl font-extrabold leading-tight mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 font-['Orbitron']">
                  {lesson.title}
                </h1>
                <div className="w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-full mb-4"></div>
              </div>

              {/* 教学目标卡片 */}
              <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-cyan-500/30 rounded-2xl p-8 backdrop-blur-md shadow-lg shadow-cyan-900/20 hover:shadow-xl hover:shadow-cyan-900/30 transition-all duration-500">
                <h3 className="flex items-center gap-3 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-6 font-['Orbitron']">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-cyan-900/50">
                    <Target className="w-5 h-5" />
                  </div>
                  🎯 学习目标
                </h3>
                {lesson.learning_objectives ? (
                  <div className="space-y-5">
                    {(() => {
                      try {
                        // 解析JSON格式的学习目标
                        const objectives = JSON.parse(lesson.learning_objectives);
                        
                        // 定义目标字段的中文映射和图标，使用更鲜艳的颜色
                        const objectiveFields = [
                          { key: '核心能力模型', label: '核心能力模型', icon: '�', color: 'neon-blue', gradient: 'from-blue-500 to-cyan-400' },
                          { key: '知识点拆解', label: '知识点拆解', icon: '🧩', color: 'neon-purple', gradient: 'from-purple-500 to-pink-400' },
                          { key: 'AI实战项目', label: 'AI实战项目', icon: '🤖', color: 'neon-green', gradient: 'from-green-500 to-emerald-400' },
                          { key: '解决痛点', label: '解决痛点', icon: '�', color: 'neon-orange', gradient: 'from-orange-500 to-amber-400' },
                          { key: '学完马上能做什么', label: '学完马上能做什么', icon: '🚀', color: 'neon-pink', gradient: 'from-pink-500 to-rose-400' },
                          { key: '未来能做的大事', label: '未来能做的大事', icon: '🌟', color: 'neon-indigo', gradient: 'from-indigo-500 to-purple-400' },
                          { key: '知识进阶路线', label: '知识进阶路线', icon: '📈', color: 'neon-teal', gradient: 'from-teal-500 to-cyan-400' }
                        ];
                        
                        // 定义鲜艳的颜色映射
                        const colorMap = {
                          'neon-blue': { 
                            bg: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20', 
                            border: 'border-blue-500/40', 
                            text: 'text-blue-300',
                            glow: 'hover:shadow-lg hover:shadow-blue-500/30'
                          },
                          'neon-purple': { 
                            bg: 'bg-gradient-to-r from-purple-500/20 to-pink-500/20', 
                            border: 'border-purple-500/40', 
                            text: 'text-purple-300',
                            glow: 'hover:shadow-lg hover:shadow-purple-500/30'
                          },
                          'neon-green': { 
                            bg: 'bg-gradient-to-r from-green-500/20 to-emerald-500/20', 
                            border: 'border-green-500/40', 
                            text: 'text-green-300',
                            glow: 'hover:shadow-lg hover:shadow-green-500/30'
                          },
                          'neon-orange': { 
                            bg: 'bg-gradient-to-r from-orange-500/20 to-amber-500/20', 
                            border: 'border-orange-500/40', 
                            text: 'text-orange-300',
                            glow: 'hover:shadow-lg hover:shadow-orange-500/30'
                          },
                          'neon-pink': { 
                            bg: 'bg-gradient-to-r from-pink-500/20 to-rose-500/20', 
                            border: 'border-pink-500/40', 
                            text: 'text-pink-300',
                            glow: 'hover:shadow-lg hover:shadow-pink-500/30'
                          },
                          'neon-indigo': { 
                            bg: 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20', 
                            border: 'border-indigo-500/40', 
                            text: 'text-indigo-300',
                            glow: 'hover:shadow-lg hover:shadow-indigo-500/30'
                          },
                          'neon-teal': { 
                            bg: 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20', 
                            border: 'border-teal-500/40', 
                            text: 'text-teal-300',
                            glow: 'hover:shadow-lg hover:shadow-teal-500/30'
                          }
                        };
                        
                        return (
                          <div className="grid grid-cols-1 gap-4">
                            {objectiveFields.map((field) => {
                              const value = objectives[field.key as keyof typeof objectives];
                              if (!value) return null;
                              
                              const colors = colorMap[field.color as keyof typeof colorMap];
                              
                              return (
                                <div 
                                  key={field.key} 
                                  className={`${colors.bg} ${colors.border} border rounded-xl p-6 transition-all duration-300 transform hover:-translate-y-1 ${colors.glow} backdrop-blur-sm`}
                                >
                                  <div className="flex items-center gap-4 mb-4">
                                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${field.gradient} flex items-center justify-center text-white shadow-lg shadow-${field.color.split('-')[1]}-500/40 border border-white/20`}>
                                      <span className="text-2xl">{field.icon}</span>
                                    </div>
                                    <div>
                                      <span className={`font-extrabold text-sm uppercase tracking-wider ${colors.text} font-['Orbitron']`}>
                                        {field.label}
                                      </span>
                                      <div className="w-20 h-1 bg-gradient-to-r from-transparent ${field.gradient} mt-2 rounded-full"></div>
                                    </div>
                                  </div>
                                  <div className="pl-16">
                                    {field.key === 'AI实战项目' ? (
                                      <ExpandableText
                                        text={value}
                                        maxLines={4}
                                        className="text-gray-200 text-sm leading-relaxed font-['Rajdhani']"
                                      />
                                    ) : (
                                      <p className="text-gray-200 text-sm leading-relaxed font-['Rajdhani']">
                                        {value}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      } catch (error) {
                        // JSON解析失败时显示原始文本，添加美化样式
                        return (
                          <div className="bg-gradient-to-r from-slate-800/80 to-slate-700/80 border border-cyan-500/30 rounded-xl p-6 backdrop-blur-sm shadow-lg">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-cyan-900/30">
                                <span className="text-xl">💾</span>
                              </div>
                              <span className="font-extrabold text-sm uppercase tracking-wider text-cyan-400 font-['Orbitron']">
                                课程信息
                              </span>
                            </div>
                            <p className="text-gray-300 leading-relaxed text-sm font-['Rajdhani']">
                              {lesson.learning_objectives}
                            </p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-slate-800/80 to-slate-700/80 border border-cyan-500/30 rounded-xl p-6 text-center backdrop-blur-sm shadow-lg">
                    <div className="mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-cyan-900/30 mx-auto">
                        <span className="text-3xl">📡</span>
                      </div>
                    </div>
                    <p className="text-gray-400 leading-relaxed text-sm font-['Rajdhani']">
                      暂无详细学习目标描述。
                    </p>
                    <div className="w-32 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent rounded-full mt-4 mx-auto"></div>
                  </div>
                )}
              </div>

              
            </div>
          </div>

          {/* 右侧：幻灯片列表 (Interactive List) */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <List className="text-blue-500" />
                课件内容
                <span className="text-sm font-normal text-gray-500 bg-white/5 px-2 py-1 rounded-md">
                  {lesson.slides.length} 页幻灯片
                </span>
              </h2>
            </div>

            <div className="space-y-3 animate-in slide-in-from-bottom-4 duration-700 delay-100">
              {lesson.slides.map((slide, index) => (
                <div
                  key={slide.id}
                  onClick={() => navigate(`/student/lesson/${lesson.id}/slide/${slide.id}`)}
                  className="group relative bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 hover:border-blue-500/30 rounded-xl p-4 transition-all duration-300 cursor-pointer flex items-center justify-between overflow-hidden"
                >
                  {/* Hover Light Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 pointer-events-none" />

                  <div className="flex items-center gap-5 z-10">
                    {/* 序号 */}
                    <div className="w-12 h-12 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center group-hover:scale-110 group-hover:border-blue-500/50 transition-all">
                      <span className="text-lg font-mono font-bold text-gray-400 group-hover:text-blue-400">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>

                    {/* 信息 */}
                    <div>
                      <h4 className="font-medium text-lg text-gray-200 group-hover:text-white transition-colors">
                        幻灯片 {slide.slide_index}
                      </h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${slide.is_final
                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                            : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                          }`}>
                          {slide.is_final ? '已定稿' : '草稿版本'}
                        </span>
                        <span className="text-xs text-gray-600">v{slide.version_number}</span>
                      </div>
                    </div>
                  </div>

                  {/* 动作按钮 */}
                  <div className="flex items-center gap-4 z-10">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-900/50 opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                      <PlayCircle className="text-white fill-current" size={20} />
                    </div>
                  </div>

                </div>
              ))}

              {lesson.slides.length === 0 && (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-dashed border-white/10">
                  <p className="text-gray-500">本课程暂未发布任何幻灯片内容。</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentLessonDetail;